import React from 'react'
import { IconButton } from '@asad/ui'
import { t } from 'i18next'
import { MenuIcon } from 'lucide-react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks'
import { readToken, readUser } from '@app/store/services/local-storage.service'
import { getProfileData } from '@app/store/slices/profile-slice'
import { doLogout } from '@app/store/slices/auth-slice'
import logo from '@app/assets/img/fud-finance-logo.png'
import darkLogo from '@app/assets/img/fud-finance-dark-logo.png.png'
import { Icon } from '@app/components/icon'

import MapBroadcastManager from '@/components/MapBroadcastManager'
import { DevicesProvider } from '@/context/DevicesContext'
import Loader from '@/components/Loader'
import { useWindowSize } from '@/hooks'
import { updateTheme } from '@/utils/utils'
import LangueSelector from '@/components/common/languageSelector/LangueSelector'
import { ThemeSwitch } from '@/features/dashboard/components/ThemeSwitch'

import { MainMenu } from './components'

const MainLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const [theme, setTheme] = React.useState<'light' | 'dark'>(() => {
    const storedTheme = localStorage.getItem('theme')
    return storedTheme === 'light' ? 'light' : 'dark'
  })

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const profile = useAppSelector((state) => state.profile?.data)
  const isLoading = useAppSelector((state) => state.profile.status === 'loading')
  const { width } = useWindowSize()

  const userId = readUser()
  const token = readToken()

  const handleToggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleThemeChange = (mode: 'light' | 'dark') => {
    setTheme(mode)
    updateTheme(mode === 'light')
  }

  React.useEffect(() => {
    updateTheme(theme === 'light')
  }, [])

  React.useEffect(() => {
    if (!token?.length || !userId?.length) {
      dispatch(doLogout())
      navigate('/auth/login', { replace: true })
    } else if (userId && !profile) dispatch(getProfileData(userId)).unwrap()
  }, [userId, token?.length, dispatch, navigate])

  React.useEffect(() => {
    if (width >= 1024 && isMenuOpen) {
      setIsMenuOpen(false)
    }
  }, [width, isMenuOpen])

  if (isLoading && !profile) {
    return (
      <div className='bg-background-general-light dark:bg-background-general-dark flex items-center justify-center min-h-screen'>
        <Loader size='80' />
      </div>
    )
  }

  const companySubItems =
    profile?.firms?.map(({ name, devices }) => {
      const devicesArray = devices?.map(({ name, id, serialNumber }) => ({
        text: name,
        path: '/device',
        id,
        serialNumber,
      }))

      return { title: name, devices: devicesArray, icon: <Icon name='ChevronDown' color='white' /> }
    }) || []

  const additionalDeviceItems = {
    title: t('main.additionalDevices'),
    devices:
      profile?.devices?.map(({ name, id, serialNumber }) => ({
        text: name,
        path: '/device',
        id,
      })) || [],
    icon: <Icon name='ChevronDown' color='white' />,
  }

  const availableDevices = [...companySubItems, additionalDeviceItems]

  return (
    <>
      <MapBroadcastManager />
      <div className='bg-background-general-light dark:bg-background-general-dark flex flex-col items-center min-h-screen h-full text-text-light dark:text-text-dark'>
        <div className='pb-14 px-4 xl:px-8 2xl:px-16 bg-background max-w-480 w-full'>
          <div className='flex flex-col w-full items-start h-auto pt-8 lg:pt-16 pb-8.25 fixed lg:static top-0 left-0 px-4 lg:px-0 bg-background/70 backdrop-blur-md z-50 shadow-md lg:shadow-none transition-all duration-300 ease-in-out'>
            <div className='flex w-full flex-col lg:flex-row justify-between items-center '>
              <div className='flex w-full justify-between items-center mb-8'>
                <img className='max-w-64' src={theme === 'dark' ? logo : darkLogo} alt='FUD' />
                <IconButton size='lg' className='lg:hidden mt-4' icon={<MenuIcon />} onClick={handleToggleMenu} />
              </div>
              <div className='w-full flex justify-end absolute top-2 lg:static right-4'>
                <div className='flex gap-2 text-sm  '>
                  <ThemeSwitch value={theme} onChange={handleThemeChange} />
                  <LangueSelector />
                </div>
              </div>
            </div>
            {isMenuOpen && width < 1024 && (
              <div className='flex justify-center w-full'>
                <MainMenu availableDevices={availableDevices} mobile={true} />
              </div>
            )}
          </div>
          <div className='flex pt-32 lg:pt-0 min-h-[calc(100vh-60px)] lg:min-h-full'>
            <div className='hidden lg:block'>
              <MainMenu availableDevices={availableDevices} />
            </div>
            <div className='lg:ml-4 xl:ml-8 w-full flex-col items-center lg:max-w-[calc(100%-288px)]'>
              <DevicesProvider>
                <Outlet />
              </DevicesProvider>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default MainLayout
